import React, { useEffect, useState } from "react";
import api, { formatTRY } from "@/lib/api";
import { AlertCircle, LoaderCircle, Clock } from "lucide-react";

const BUCKETS = [
    { key: "0-30", label: "0-30 gün", color: "emerald" },
    { key: "31-60", label: "31-60 gün", color: "amber" },
    { key: "61-90", label: "61-90 gün", color: "orange" },
    { key: "90+", label: "90+ gün", color: "rose" },
];
const CS = {
    emerald: "bg-emerald-50 dark:bg-emerald-950/30 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800/40",
    amber: "bg-amber-50 dark:bg-amber-950/30 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-800/40",
    orange: "bg-orange-50 dark:bg-orange-950/30 text-orange-700 dark:text-orange-300 border-orange-200 dark:border-orange-800/40",
    rose: "bg-rose-50 dark:bg-rose-950/30 text-rose-700 dark:text-rose-300 border-rose-200 dark:border-rose-800/40",
};

export default function AgingTab() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [bucketFilter, setBucketFilter] = useState("all");

    useEffect(() => {
        setLoading(true);
        api.get("/admin/reports/aging").then((r) => setData(r.data)).finally(() => setLoading(false));
    }, []);

    if (loading) return <div className="p-8 text-center"><LoaderCircle className="h-6 w-6 mx-auto animate-spin text-violet-500" /></div>;
    if (!data) return null;

    const shown = data.details.filter((d) => bucketFilter === "all" || d.bucket === bucketFilter);

    return (
        <div data-testid="tab-aging">
            {/* Bucket kartları */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                {BUCKETS.map((b) => {
                    const v = data.buckets[b.key] || { count: 0, amount: 0 };
                    const active = bucketFilter === b.key;
                    return (
                        <button
                            key={b.key}
                            onClick={() => setBucketFilter(active ? "all" : b.key)}
                            data-testid={`aging-bucket-${b.key}`}
                            className={`p-3 rounded-xl border transition-all ${CS[b.color]} ${active ? "ring-2 ring-offset-2 dark:ring-offset-slate-950 ring-current" : ""}`}
                        >
                            <div className="flex items-center gap-2 mb-1">
                                <Clock className="h-3.5 w-3.5" />
                                <span className="text-[10px] font-bold uppercase tracking-widest">{b.label}</span>
                            </div>
                            <div className="text-xl font-black">{v.count}</div>
                            <div className="text-[11px] font-semibold">{formatTRY(v.amount)}</div>
                        </button>
                    );
                })}
            </div>

            {/* Toplam */}
            <div className="mb-3 p-3 rounded-xl bg-slate-100 dark:bg-slate-800/50 flex items-center gap-3">
                <AlertCircle className="h-4 w-4 text-rose-500" />
                <div className="text-sm">
                    <b>Toplam bekleyen tahsilat:</b> {data.total_count} rezervasyon · <b className="text-rose-600 dark:text-rose-400">{formatTRY(data.total_amount)}</b>
                </div>
                {bucketFilter !== "all" && <button onClick={() => setBucketFilter("all")} className="ml-auto text-xs font-semibold text-violet-600 hover:underline">Filtreyi temizle</button>}
            </div>

            {/* Detay tablosu */}
            <div className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 overflow-hidden">
                <div className="overflow-x-auto max-h-[65vh] overflow-y-auto">
                    <table className="w-full text-sm">
                        <thead className="sticky top-0 bg-slate-50 dark:bg-slate-900/95 text-[10px] font-bold uppercase tracking-widest text-slate-500">
                            <tr>
                                <th className="text-left px-3 py-2">Voucher</th>
                                <th className="text-left px-3 py-2">Otel</th>
                                <th className="text-left px-3 py-2">Misafir</th>
                                <th className="text-right px-3 py-2">Toplam</th>
                                <th className="text-right px-3 py-2">Ödenen</th>
                                <th className="text-right px-3 py-2">Kalan</th>
                                <th className="text-left px-3 py-2">Yaş</th>
                            </tr>
                        </thead>
                        <tbody>
                            {shown.length === 0 ? (
                                <tr><td colSpan={7} className="py-12 text-center text-slate-500">Bu bucket için kayıt yok 🎉</td></tr>
                            ) : shown.map((r) => (
                                <tr key={r.booking_id} className="border-t border-slate-100 dark:border-slate-800/50 hover:bg-slate-50 dark:hover:bg-slate-900/40" data-testid={`aging-row-${r.booking_id}`}>
                                    <td className="px-3 py-2 font-mono text-[11px]">{r.voucher_no}</td>
                                    <td className="px-3 py-2 text-xs">{r.hotel_name}</td>
                                    <td className="px-3 py-2">
                                        <div className="font-semibold">{r.guest_name}</div>
                                        <div className="text-[10px] text-slate-500">{r.guest_phone}</div>
                                    </td>
                                    <td className="px-3 py-2 text-right text-xs">{formatTRY(r.total)}</td>
                                    <td className="px-3 py-2 text-right text-xs text-emerald-600">{formatTRY(r.paid)}</td>
                                    <td className="px-3 py-2 text-right font-bold text-rose-600 dark:text-rose-400">{formatTRY(r.remaining)}</td>
                                    <td className="px-3 py-2 text-[11px]"><span className={`px-2 py-0.5 rounded-md font-bold ${CS[BUCKETS.find((b) => b.key === r.bucket)?.color]}`}>{r.age_days} gün</span></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}
